import { Feedback } from '@/types/feedback';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { voteBadgeVariant, voteLabel } from '@/lib/vote';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';

interface FeedbackSheetProps {
  open: boolean;
  feedback: Feedback | null;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackSheet({ open, feedback, onOpenChange }: FeedbackSheetProps) {
  if (!feedback) return null;

  const formatTimestamp = (timestamp: string): string => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZoneName: 'short',
      });
    } catch {
      return timestamp;
    }
  };

  const renderRating = (rating: number): string => {
    const stars = '★'.repeat(Math.max(0, Math.min(5, rating)));
    const emptyStars = '☆'.repeat(5 - Math.max(0, Math.min(5, rating)));
    return `${stars}${emptyStars}`;
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[400px] sm:w-[540px] sm:max-w-[540px] overflow-y-auto p-6">
        <SheetHeader>
          <SheetTitle>Feedback Details</SheetTitle>
          <SheetDescription>
            {feedback.product} - {feedback.feature}
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Feedback Content */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Feedback</h4>
            <div className="flex items-center gap-3">
              <Badge variant={voteBadgeVariant(feedback.vote)}>
                {voteLabel(feedback.vote)}
              </Badge>
              <span className="text-warning-500" title={`Rating: ${feedback.rating}/5`}>
                {renderRating(feedback.rating)}
              </span>
            </div>
            {feedback.feedback ? (
              <p className="text-sm leading-relaxed bg-muted p-3 rounded-md">
                {feedback.feedback}
              </p>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                No feedback text provided
              </p>
            )}
          </div>

          <Separator />

          {/* User Information */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">User Information</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground">Customer</p>
                <p className="font-medium cx-mask">{feedback.customer || '—'}</p>
              </div>
              <div>
                <p className="text-muted-foreground">User</p>
                <p className="font-medium truncate cx-mask" title={feedback.user}>
                  {feedback.user}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Role</p>
                <p className="font-medium">{feedback.userRole}</p>
              </div>
              <div>
                <p className="text-muted-foreground">IP Address</p>
                <p className="font-medium font-mono text-xs cx-mask">{feedback.remoteIpAddress}</p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Metadata */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-muted-foreground">Metadata</h4>
            <div className="space-y-2 text-sm">
              <div>
                <p className="text-muted-foreground">Timestamp</p>
                <p className="font-medium">{formatTimestamp(feedback.timestamp)}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Partition Key</p>
                <p className="font-mono text-xs text-muted-foreground truncate" title={feedback.partitionKey}>
                  {feedback.partitionKey}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Row Key</p>
                <p className="font-mono text-xs text-muted-foreground truncate" title={feedback.rowKey}>
                  {feedback.rowKey}
                </p>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
